/* eslint-disable @typescript-eslint/no-empty-function */
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { Grid, IconButton } from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import React, { useEffect, useState } from 'react'
import { Control, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import CONSTANTS, { PAGES_ROUTES, VALIDATIONS } from '../../../Constants'
import {
  combineErrorMessage,
  convertBooleanIntoYesAndNo,
  createAttributePayload,
  isValidArray,
  isValidJSON,
  returnValidValue,
  stringifyIfNotNull,
} from '../../../Helpers/Util'
import updateTagMetaData from '../../../Helpers/updateTagMetaData'
import { useAppDispatch, useAppSelector } from '../../../Store/Hooks'
import { showAlert } from '../../../Store/Slice/alert.slice'
import { selectDataSource } from '../../../Store/Slice/asset-definition.slice'
import {
  createAttributeService,
  editAttributeService,
  getAttributeService,
  selectEditAttributeData,
  selectFieldTypesList,
  selectNonComplexTypeAttributesList,
  selectaffiliatePartnersList,
  setAttributeData,
} from '../../../Store/Slice/attribute.slice'
import { BulbIcon } from '../../Icons'
import { MUIButton } from '../../Shared'
import MUIAutocompleteMultiSelect from '../../Shared/MUI-AutocompleteMultiSelect'
import MUIBox from '../../Shared/MUI-Box'
import MUIField from '../../Shared/MUI-Field'
import MUISelect from '../../Shared/MUI-Select'
import MUITooltip from '../../Shared/MUI-Tooltip'
import style from './AttributeEditor.module.scss'

interface AttributeDetailFormPropsInterface {
  type: string
  attributeId?: string
  setAttributeLabel?: any
}

const AttributeDetailForm = ({
  type = '',
  attributeId = '',
  setAttributeLabel = () => {},
}: AttributeDetailFormPropsInterface) => {
  const { MetaMaster, AttributeRepository, PageTypeView } = PAGES_ROUTES
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const masterDataListStore: any[] = useAppSelector(selectDataSource)
  const masterDataListWithCustomOption = JSON.parse(JSON.stringify(masterDataListStore || []))
  if (masterDataListWithCustomOption?.length) {
    masterDataListWithCustomOption.sort((a: any, b: any) => a.value.localeCompare(b.value))
    masterDataListWithCustomOption.unshift({
      value: 'Custom Option',
      key: 'customOption',
    })
  }
  const allNonComplexFieldTypes: [] = useAppSelector(selectNonComplexTypeAttributesList)
  const allFieldTypes: [] = useAppSelector(selectFieldTypesList)
  const affiliatePartnerList = useAppSelector(selectaffiliatePartnersList)
  const attributeData = useAppSelector(selectEditAttributeData)
  const [attributeRefData, setAttributeRefData] = useState<any>([])
  const [selectedAffiliatePartnerList, setSelectedAffiliatePartnerList] = useState<any>([])
  const [toggleFields, setToggleFields] = useState<any>({
    metaAttributes: false,
    affiliatePartners: false,
    options: true,
    master: true,
    isMultiple: true,
    defaultValue: true,
    placeholder: true,
    sampleValue: false,
  })
  const [showOptionField, setShowOptionField] = useState(false)
  const [masterDataList] = useState(masterDataListWithCustomOption)

  const {
    control,
    register,
    unregister,
    handleSubmit,
    watch,
    getValues,
    setFocus,
    setValue,
    setError,
    clearErrors,
    trigger,
    formState: { errors, isValid },
  } = useForm<any>({
    mode: 'onChange',
    defaultValues: {
      fieldType: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        attributeData?.fieldType,
        null,
      ),
      dataType: returnValidValue(type === CONSTANTS.PAGE_TYPE.EDIT, attributeData?.dataType, null),
      fieldName: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        attributeData?.fieldName,
        null,
      ),
      isRequired: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        convertBooleanIntoYesAndNo(attributeData?.isRequired),
        'Yes',
      ),
      isMultiple: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        convertBooleanIntoYesAndNo(attributeData?.isMultiple),
        'No',
      ),
      label: returnValidValue(type === CONSTANTS.PAGE_TYPE.EDIT, attributeData?.label, null),
      isB2B: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        convertBooleanIntoYesAndNo(attributeData?.isB2B),
        'No',
      ),
      defaultValue: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        attributeData?.defaultValue,
        null,
      ),
      affiliatePartner: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        attributeData?.affiliatePartner,
        [],
      ),
      placeholder: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        attributeData?.placeholder,
        null,
      ),
      master: returnValidValue(type === CONSTANTS.PAGE_TYPE.EDIT, attributeData?.master, null),
      options: returnValidValue(type === CONSTANTS.PAGE_TYPE.EDIT, attributeData?.options, null),
      addMore: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        convertBooleanIntoYesAndNo(attributeData?.addMore),
        'No',
      ),
      isBulkUpdate: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        convertBooleanIntoYesAndNo(attributeData?.isBulkUpdate),
        'No',
      ),
      isAdvancedSearch: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        convertBooleanIntoYesAndNo(attributeData?.isAdvancedSearch),
        'Yes',
      ),
      sampleValue: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        attributeData?.sampleValue,
        null,
      ),
      isEMA: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        convertBooleanIntoYesAndNo(attributeData?.isEMA),
        'No',
      ),
      status: returnValidValue(type === CONSTANTS.PAGE_TYPE.EDIT, attributeData?.status, 'ACTIVE'),
      isMultilingual: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        convertBooleanIntoYesAndNo(attributeData?.isMultilingual),
        'No',
      ),
      esField:
        type === CONSTANTS.PAGE_TYPE.EDIT && attributeData?.esField ? attributeData.esField : null,
      description: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        attributeData?.description,
        null,
      ),
      additionalProperties: returnValidValue(
        type === CONSTANTS.PAGE_TYPE.EDIT,
        stringifyIfNotNull(attributeData?.additionalProperties),
        '',
      ),
      attributeRef: [],
      metaCategory: type === CONSTANTS.PAGE_TYPE.EDIT ? attributeData?.metaCategory : null,
      example:
        type === CONSTANTS.PAGE_TYPE.EDIT && attributeData?.example
          ? Object.entries(attributeData.example).map(([platform, description]) => ({
              platform,
              description,
            }))
          : [{ platform: '', description: '' }],
    },
    shouldUnregister: true,
  })

  const {
    fields: exampleFields,
    append: appendExampleField,
    remove: removeExampleField,
  } = useFieldArray({
    control,
    name: 'example',
  })

  const getAttributeData = async () => {
    const { payload = {} } = await dispatch(getAttributeService(attributeId) as any)
    const { data = {} } = payload
    setAttributeLabel(data?.label || '')
  }
  useEffect(() => {
    if (attributeId) {
      getAttributeData()
    }
    if (type === CONSTANTS.PAGE_TYPE.CREATE) {
      setToggleFields({
        ...toggleFields,
        metaAttributes: false,
      })
      setAttributeRefData([])
      dispatch(setAttributeData({}))
    }
    if (type === CONSTANTS.PAGE_TYPE.EDIT) {
      handleEditMode()
    }
  }, [])

  useEffect(() => {
    if (type !== CONSTANTS.PAGE_TYPE.CREATE) {
      if (attributeData?.master) setValue('master', attributeData?.master)
      if (attributeData?.master === '' && attributeData?.options?.length) {
        setValue('master', 'customOption')
      }
    }
  }, [masterDataList])

  useEffect(() => {
    updateTagMetaData({
      title: attributeId ? `Attribute: ${attributeData?.label}` : 'Create Attribute',
    })
    return () => {
      updateTagMetaData({})
    }
  }, [attributeId])

  const handleEditMode = () => {
    let copyToggleFields = JSON.parse(JSON.stringify(toggleFields))
    const fieldTypeValue: string = getValues('fieldType') || ''
    if (fieldTypeValue === 'COMPLEXTYPE') {
      copyToggleFields = {
        ...copyToggleFields,
        metaAttributes: true,
        options: false,
        master: false,
        isMultiple: false,
        defaultValue: false,
        placeholder: false,
      }
      register('attributeRef', { required: 'This field is required' })
      unregister('options')
      unregister('master')
      unregister('isMultiple')
      unregister('defaultValue')
      unregister('placeholder')
      setFocus('dataType')
      if (isValidArray(attributeData?.attributeRef)) {
        setAttributeRefData(attributeData?.attributeRef)
        setValue('attributeRef', attributeData?.attributeRef)
      }
    }
    const isB2BValue = getValues('isB2B') || ''
    if (isB2BValue === 'Yes') {
      copyToggleFields = {
        ...copyToggleFields,
        affiliatePartners: true,
      }
      const selectedAffiliatePartners: any = []
      if (isValidArray(attributeData?.affiliatePartner) && isValidArray(affiliatePartnerList)) {
        attributeData?.affiliatePartner?.forEach((element: string) => {
          const matchedValue = affiliatePartnerList?.find((x: any) => x?.key === element)
          selectedAffiliatePartners.push(matchedValue)
        })
        if (isValidArray(selectedAffiliatePartners)) {
          setTimeout(() => {
            setSelectedAffiliatePartnerList(selectedAffiliatePartners)
            setValue('affiliatePartner', selectedAffiliatePartners)
          }, 500)
        }
      }
    }
    const dataTypeValue: string = getValues('dataType') || ''
    if (
      fieldTypeValue !== 'COMPLEXTYPE' &&
      fieldTypeValue === 'SELECT' &&
      dataTypeValue === 'BOOLEAN'
    ) {
      copyToggleFields = {
        ...copyToggleFields,
        options: false,
      }
      unregister('options')
    }
    const isBulkUpdateValue = getValues('isBulkUpdate') || ''
    if (isBulkUpdateValue === 'Yes') {
      copyToggleFields = {
        ...copyToggleFields,
        sampleValue: true,
      }
      setValue('sampleValue', attributeData?.sampleValue)
    }
    setToggleFields({
      ...copyToggleFields,
    })
    if (attributeData?.master) setValue('master', attributeData?.master)
    if (attributeData?.master === '' && attributeData?.options?.length) {
      setValue('master', 'customOption')
    }
  }

  const watchedFieldType = watch('fieldType')
  const watchedIsB2B = watch('isB2B')
  const watchedIsBulkUpdate = watch('isBulkUpdate')
  const watchedDataType = watch('dataType')
  const watchedMaster = watch('master')

  useEffect(() => {
    let updated = { ...toggleFields }

    if (watchedFieldType === 'COMPLEXTYPE') {
      updated = {
        ...updated,
        metaAttributes: true,
        options: false,
        master: false,
        isMultiple: false,
        defaultValue: false,
        placeholder: false,
      }
      register('attributeRef', { required: 'This field is required' })
      unregister(['options', 'master', 'isMultiple', 'defaultValue', 'placeholder'])
      setFocus('dataType')

      if (isValidArray(attributeData?.attributeRef)) {
        setAttributeRefData(attributeData.attributeRef)
        setValue('attributeRef', attributeData.attributeRef)
      }
    } else {
      updated = {
        ...updated,
        metaAttributes: false,
        options: true,
        master: true,
        isMultiple: true,
        defaultValue: true,
        placeholder: true,
      }
      unregister('attributeRef')
      register('options')
      register('master')
      register('isMultiple', { required: 'This field is required' })
      register('defaultValue')
      register('placeholder', { required: 'This field is required' })
    }

    setToggleFields(updated)
  }, [watchedFieldType, watchedDataType])

  useEffect(() => {
    if (watchedIsB2B === 'Yes') {
      setToggleFields((prev: any) => ({ ...prev, affiliatePartners: true }))
      register('affiliatePartner', { required: 'This field is required' })
    } else {
      setToggleFields((prev: any) => ({ ...prev, affiliatePartners: false }))
      unregister('affiliatePartner')
    }
  }, [watchedIsB2B])

  useEffect(() => {
    if (watchedIsBulkUpdate === 'Yes') {
      setToggleFields((prev: any) => ({ ...prev, sampleValue: true }))
      register('sampleValue')
    } else {
      setToggleFields((prev: any) => ({ ...prev, sampleValue: false }))
      setValue('sampleValue', '')
      unregister('sampleValue')
    }
  }, [watchedIsBulkUpdate])

  useEffect(() => {
    setShowOptionField(watchedMaster === 'customOption')
  }, [watchedMaster])

  const handleMetaAttributeChange = (event: any, newValue: any[], reason: any) => {
    if (reason === CONSTANTS.AUTOCOMPLETE_EVENT_TYPES.INPUT) {
      return
    }
    if (reason === CONSTANTS.AUTOCOMPLETE_EVENT_TYPES.CLEAR) {
      setValue('attributeRef', [])
      setAttributeRefData([])
    }
    if (
      reason === CONSTANTS.AUTOCOMPLETE_EVENT_TYPES.SELECTOPTION ||
      reason === CONSTANTS.AUTOCOMPLETE_EVENT_TYPES.REMOVEOPTION
    ) {
      setValue('attributeRef', newValue)
      setAttributeRefData(newValue)
      newValue?.length > 0
        ? clearErrors('attributeRef')
        : setError('attributeRef', {
            types: {
              required: 'This field is required',
            },
          })
    }
  }

  const handleAffiliatePartnerChange = async (event: any, newValue: any[], reason: any) => {
    if (reason === CONSTANTS.AUTOCOMPLETE_EVENT_TYPES.INPUT) {
      return
    }
    if (reason === CONSTANTS.AUTOCOMPLETE_EVENT_TYPES.CLEAR) {
      setValue('affiliatePartner', [])
      setSelectedAffiliatePartnerList([])
    }
    if (
      reason === CONSTANTS.AUTOCOMPLETE_EVENT_TYPES.SELECTOPTION ||
      reason === CONSTANTS.AUTOCOMPLETE_EVENT_TYPES.REMOVEOPTION
    ) {
      setValue('affiliatePartner', newValue)
      setSelectedAffiliatePartnerList(newValue)
      const validationResult = await trigger('affiliatePartner')
      if (validationResult) {
        clearErrors('affiliatePartner')
      } else {
        setError('affiliatePartner', {
          types: {
            required: 'This field is required',
          },
        })
      }
    }
  }

  const createAttribute = async (data: any) => {
    const body = createAttributePayload(data, CONSTANTS.PAGE_TYPE.CREATE)
    const result = await dispatch(createAttributeService(body) as any)
    return result
  }

  const editAttribute = async (data: any) => {
    let body = createAttributePayload(data, CONSTANTS.PAGE_TYPE.EDIT)
    body = {
      ...body,
      attributeId,
    }
    const result = await dispatch(editAttributeService(body) as any)
    return result
  }

  const validatePlatformExamples = (
    examples: { platform: string; description: string }[],
  ): string | null => {
    const seenPlatforms = new Set<string>()
    const errorMessage =
      'Each platform example must include both fields. The `platform` key must contain only alphanumeric characters and colons, and there must be no duplicate platforms.'

    for (const { platform: rawPlatform, description: rawDescription } of examples) {
      const platform = rawPlatform.trim()
      const description = rawDescription.trim()

      // Both fields required
      if (!platform || !description) return errorMessage

      // Allowed characters: alphanumeric, spaces, colons
      if (!/^[a-zA-Z0-9\s:]+$/.test(platform)) return errorMessage

      // Duplicate check (case-insensitive)
      const platformKey = platform.toLowerCase()
      if (seenPlatforms.has(platformKey)) return errorMessage

      seenPlatforms.add(platformKey)
    }

    return null // No errors
  }

  const onSubmit = async (data: any) => {
    const { additionalProperties = '' } = data
    if (additionalProperties !== '' && !isValidJSON(additionalProperties)) {
      setError('additionalProperties', {
        type: 'manual',
        message: 'Invalid JSON value',
      })
      return
    }
    if (data.example && data.example.length > 0) {
      // Remove items where both fields are empty
      data.example = data.example.filter(
        (item: any) => item.platform?.trim() !== '' || item.description?.trim() !== '',
      )
      const platformExampleError = validatePlatformExamples(data.example)
      if (platformExampleError) {
        dispatch(showAlert([true, platformExampleError, 'error']))
        return
      }
    }

    let result: any
    if (attributeId && type === CONSTANTS.PAGE_TYPE.EDIT) {
      result = await editAttribute(data)
    }
    if (type === CONSTANTS.PAGE_TYPE.CREATE) {
      result = await createAttribute(data)
    }
    const unwrappableRes = unwrapResult(result)
    if (unwrappableRes?.error) {
      dispatch(showAlert([true, combineErrorMessage(unwrappableRes), 'error']))
      return
    }
    dispatch(showAlert([true, unwrappableRes.message, 'success']))
    if (attributeId && type === CONSTANTS.PAGE_TYPE.EDIT) {
      navigate(`/${MetaMaster}/${AttributeRepository}/${PageTypeView}/${attributeId}`)
    }
    if (type === CONSTANTS.PAGE_TYPE.CREATE) {
      navigate(`/${MetaMaster}/${AttributeRepository}`)
    }
  }
  return (
    <MUIBox>
      <div>
        <div className='heading-6 d-flex text-left rolebox__header-padding'>Attribute Contents</div>
        <div className={'hr'}></div>
        {attributeData?.isAssigned ? (
          <div className={style.tip__box}>
            <div className={style.tip__box_icon}>
              <div className='text-center paragraph-regular-3'>
                <BulbIcon />
              </div>
              <div className='text-center'>Tip</div>
            </div>
            <div className={style.tip__box_content}>
              Certain fields will be non-editable due to the assignment of this attribute to an
              asset type.
            </div>
          </div>
        ) : (
          <></>
        )}
        <div className='rolebox__content-padding'>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container columnSpacing={10} rowSpacing={4}>
              <Grid item xs={6} md={6} lg={6} xl={6}>
                <MUIField
                  name='fieldName'
                  label='Field Name'
                  required
                  type='text'
                  disabled={attributeData?.isAssigned}
                  rules={{
                    required: VALIDATIONS.REQUIRED,
                  }}
                  control={control as Control | undefined}
                  error={errors}
                  maxLength={100}
                />
              </Grid>
              <Grid item xs={6} md={6} lg={6} xl={6}>
                <MUIField
                  name='label'
                  label='Label'
                  required
                  type='text'
                  rules={{
                    required: VALIDATIONS.REQUIRED,
                  }}
                  control={control as Control | undefined}
                  error={errors}
                  maxLength={100}
                />
              </Grid>
              {!!allFieldTypes?.length && (
                <Grid item xs={6} md={6} lg={6} xl={6}>
                  <MUISelect
                    name='fieldType'
                    lable={'Field Type'}
                    required
                    disabled={attributeData?.isAssigned}
                    rules={{ required: VALIDATIONS.REQUIRED }}
                    control={control as Control | undefined}
                    error={errors}
                    labelKey={'key'}
                    valueKey={'value'}
                    options={allFieldTypes || []}
                  />
                </Grid>
              )}
              <Grid item xs={6} md={6} lg={6} xl={6}>
                <MUISelect
                  name='dataType'
                  lable={'Data Type'}
                  required
                  disabled={attributeData?.isAssigned}
                  rules={{ required: VALIDATIONS.REQUIRED }}
                  control={control as Control | undefined}
                  error={errors}
                  labelKey={'key'}
                  valueKey={'value'}
                  options={CONSTANTS.DATA_TYPE_LIST}
                />
              </Grid>
              <Grid item xs={6} md={6} lg={6} xl={6}>
                <MUIField
                  name='esField'
                  label='ES Field'
                  required
                  disabled={attributeData?.isAssigned}
                  type='text'
                  rules={{
                    required: VALIDATIONS.REQUIRED,
                  }}
                  control={control as Control | undefined}
                  error={errors}
                  maxLength={100}
                />
              </Grid>
              <Grid item xs={6} md={6} lg={6} xl={6}>
                <MUISelect
                  name='isRequired'
                  lable={'Required'}
                  required
                  rules={{ required: VALIDATIONS.REQUIRED }}
                  control={control as Control | undefined}
                  error={errors}
                  options={CONSTANTS.YES_NO_LIST}
                />
              </Grid>
              <Grid item xs={6} md={6} lg={6} xl={6}>
                <MUISelect
                  name='status'
                  lable={'Status'}
                  required
                  disabled={attributeData?.isAssigned}
                  rules={{ required: VALIDATIONS.REQUIRED }}
                  control={control as Control | undefined}
                  error={errors}
                  options={CONSTANTS.STATUS_LIST}
                />
              </Grid>
              <Grid item xs={6} md={6} lg={6} xl={6}>
                <MUISelect
                  name='isMultilingual'
                  lable={'Is Multilingual'}
                  control={control as Control | undefined}
                  error={errors}
                  options={CONSTANTS.YES_NO_LIST}
                />
              </Grid>
              {toggleFields.placeholder && (
                <Grid item xs={6} md={6} lg={6} xl={6}>
                  <MUIField
                    name='placeholder'
                    label='Placeholder'
                    required
                    type='text'
                    rules={{ required: VALIDATIONS.REQUIRED }}
                    control={control as Control | undefined}
                    error={errors}
                    maxLength={100}
                  />
                </Grid>
              )}
              <Grid item xs={6} md={6} lg={6} xl={6}>
                <MUISelect
                  name='isB2B'
                  lable={'B2B'}
                  required
                  rules={{ required: VALIDATIONS.REQUIRED }}
                  control={control as Control | undefined}
                  error={errors}
                  options={CONSTANTS.YES_NO_LIST}
                />
              </Grid>
              <Grid item xs={6} md={6} lg={6} xl={6}>
                <MUISelect
                  name='addMore'
                  lable={'Add More'}
                  disabled={attributeData?.isAssigned}
                  control={control as Control | undefined}
                  error={errors}
                  options={CONSTANTS.YES_NO_LIST}
                />
              </Grid>
              <Grid item xs={6} md={6} lg={6} xl={6}>
                <MUISelect
                  name='isBulkUpdate'
                  lable={'Is Bulk Update'}
                  control={control as Control | undefined}
                  error={errors}
                  options={CONSTANTS.YES_NO_LIST}
                />
              </Grid>
              <Grid item xs={6} md={6} lg={6} xl={6}>
                <MUISelect
                  name='isAdvancedSearch'
                  lable={'Is Advanced Search'}
                  control={control as Control | undefined}
                  error={errors}
                  options={CONSTANTS.YES_NO_LIST}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={12} xl={12}>
                <MUIField
                  name='description'
                  label='Description'
                  required
                  type='text'
                  rules={{ required: VALIDATIONS.REQUIRED }}
                  control={control as Control | undefined}
                  error={errors}
                  maxLength={300}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={12} xl={12}>
                <MUIField
                  name='additionalProperties'
                  label='Additional Properties'
                  type='text'
                  control={control as Control | undefined}
                  error={errors}
                  maxLength={300}
                />
              </Grid>
              {toggleFields.defaultValue && (
                <Grid item xs={6} md={6} lg={6} xl={6}>
                  <MUIField
                    name='defaultValue'
                    label='Default Value'
                    type='text'
                    control={control as Control | undefined}
                    error={errors}
                    maxLength={5000}
                  />
                </Grid>
              )}
              {toggleFields.isMultiple && (
                <Grid item xs={6} md={6} lg={6} xl={6}>
                  <MUISelect
                    name='isMultiple'
                    lable={'Multi Select'}
                    required
                    disabled={attributeData?.isAssigned}
                    rules={{ required: VALIDATIONS.REQUIRED }}
                    control={control as Control | undefined}
                    error={errors}
                    options={CONSTANTS.YES_NO_LIST}
                  />
                </Grid>
              )}
              {toggleFields.master && masterDataListWithCustomOption?.length > 0 && (
                <Grid item xs={6} md={6} lg={6} xl={6}>
                  <MUISelect
                    name='master'
                    lable={'Master'}
                    disabled={attributeData?.isAssigned}
                    control={control as Control | undefined}
                    error={errors}
                    options={masterDataListWithCustomOption || []}
                    labelKey={'value'}
                    valueKey={'key'}
                  />
                </Grid>
              )}
              {showOptionField && (
                <Grid item xs={6} md={6} lg={6} xl={6}>
                  <MUIField
                    name='options'
                    label='Options'
                    type='text'
                    control={control as Control | undefined}
                    error={errors}
                    maxLength={2000}
                  />
                </Grid>
              )}

              {toggleFields.metaAttributes && (
                <Grid item xs={6} md={6} lg={6} xl={6}>
                  <MUIAutocompleteMultiSelect
                    usingFor={'metaAttributes'}
                    label={'Meta Attributes'}
                    required={true}
                    LabelIcon={null}
                    labelKey='label'
                    name='attributeRef'
                    disabled={attributeData?.isAssigned}
                    options={allNonComplexFieldTypes || []}
                    control={control as Control | undefined}
                    error={errors}
                    value={attributeRefData}
                    onInputChange={handleMetaAttributeChange}
                    rules={{ required: VALIDATIONS.REQUIRED }}
                    limitTags={2}
                    id={'attributeRef'}
                  />
                </Grid>
              )}

              {toggleFields.affiliatePartners && (
                <Grid item xs={6} md={6} lg={6} xl={6}>
                  <MUIAutocompleteMultiSelect
                    usingFor={'attributePartnersName'}
                    label={'Partner Name'}
                    required={true}
                    LabelIcon={null}
                    labelKey='key'
                    name='affiliatePartner'
                    options={affiliatePartnerList ?? []}
                    control={control as Control | undefined}
                    error={errors}
                    value={selectedAffiliatePartnerList}
                    onInputChange={handleAffiliatePartnerChange}
                    rules={{ required: VALIDATIONS.REQUIRED }}
                    limitTags={2}
                    id={'affiliatePartner'}
                  />
                </Grid>
              )}
              <Grid item xs={6} md={6} lg={6} xl={6}>
                <MUISelect
                  name='isEMA'
                  lable={'Is EMA Field'}
                  disabled={attributeData?.isAssigned}
                  control={control as Control | undefined}
                  error={errors}
                  options={CONSTANTS.YES_NO_LIST}
                />
              </Grid>
              {toggleFields.sampleValue && (
                <Grid item xs={6} md={6} lg={6} xl={6}>
                  <MUIField
                    name='sampleValue'
                    label='Sample Value in CSV'
                    type='text'
                    control={control as Control | undefined}
                    error={errors}
                    maxLength={5000}
                  />
                </Grid>
              )}
              <Grid container className='text-left' sx={{ rowGap: '12px !important' }}>
                <Grid item xs={12} className='mt-neg12'>
                  <div className='title__style'>
                    Platform Examples (e.g., WEB, APP, TV, B2B, etc.)
                  </div>
                </Grid>
                {exampleFields.map((item, index) => (
                  <Grid
                    item
                    xs={12}
                    className={style.example__box}
                    key={`example.${item?.id}.platform`}
                  >
                    <Grid item xs className={style.field__box}>
                      <Grid item xs={6}>
                        <MUIField
                          name={`example.${index}.platform`}
                          label='Platform'
                          type='text'
                          control={control as Control | undefined}
                          error={errors}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <MUIField
                          name={`example.${index}.description`}
                          label='Description'
                          type='text'
                          control={control as Control | undefined}
                          error={errors}
                        />
                      </Grid>
                    </Grid>

                    <Grid item>
                      <IconButton
                        className='icon-button'
                        data-testid='add__icon_complextype'
                        sx={{ marginX: '0 !important' }}
                        onClick={() =>
                          index === 0
                            ? appendExampleField({ platform: '', description: '' })
                            : removeExampleField(index)
                        }
                      >
                        <MUITooltip
                          title={index === 0 ? 'Add New Value' : 'Remove Value'}
                          disableInteractive
                        >
                          {index === 0 ? (
                            <AddCircleOutlineIcon className='fs-25' />
                          ) : (
                            <RemoveCircleOutlineIcon className='fs-25' />
                          )}
                        </MUITooltip>
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <div className='submit__handler'>
              <div className='submit__handler-btn-cont'>
                <MUIButton
                  disabled={!isValid && type === CONSTANTS.PAGE_TYPE.CREATE}
                  size='large'
                  type={'submit'}
                  label={'Submit'}
                  variant='contained'
                  width={200}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </MUIBox>
  )
}

export default React.memo(AttributeDetailForm)
